import {
  parseQuery,
  DefaultQueryParams,
  environmentBuilder,
  GameLiveConnection,
} from '@pascal/pascal-core';
import { popupStore } from '@pascal/pascal-core/shared/core/store';
import { gameService } from './GameService';
import { gameName, socketGamePath, socketTurboPath } from '../enums/gameConfigsAndUrls';
import { CONNECTION_METHOD_NAMES, TURBO_GAME_ID } from '../enums/constants';
import { turboGameService } from './TurboGameService';

let connectionCount = 0;

export class GameConnection extends GameLiveConnection {
  constructor(url) {
    super(url);
  }

  async sendGameGetInitialState(isFirst = true) {
    const {
      [DefaultQueryParams.GameTypeId]: gameTypeId,
      [DefaultQueryParams.PartnerId]: partnerId,
      [DefaultQueryParams.GameId]: gameId,
    } = parseQuery(window.location.search);
    const isTurbo = +gameId === TURBO_GAME_ID;

    super.onGetInitialStateResult(this.handleInitialResult.bind(this));
    await super.sendGetInitialState(isTurbo ? { partnerId, gameId } : { partnerId, gameTypeId });
    if (isFirst) {
      if (isTurbo) {
        this.executeTurboGameConnections();
      } else {
        this.executeGameConnections();
      }
      this.executeGlobalConnections();
    }
  }

  handleInitialResult(data) {
    const {
      [DefaultQueryParams.GameTypeId]: gameTypeId,
      [DefaultQueryParams.PartnerId]: partnerId,
      [DefaultQueryParams.GameId]: gameId,
    } = parseQuery(window.location.search);
    const isTurbo = +gameId === TURBO_GAME_ID;

    if (!data || (!data.roundInfo && !data.gameTypeInfo && !isTurbo)) {
      if (connectionCount > 2) {
        // popupStore.open({ name: PopUps.ConnectionError });
        popupStore.onConnectionError(true);
      } else {
        setTimeout(() => {
          connectionCount += 1;
          super
            .sendGetInitialState(isTurbo ? { partnerId, gameId } : { partnerId, gameTypeId })
            .then(() => {});
        }, 1500);
      }
    } else if (isTurbo) {
      turboGameService.onGetInitialStateResult.call(turboGameService, data);
    } else {
      gameService.onGetInitialStateResult.call(gameService, data);
    }
  }

  onLiveBetsResult(handler) {
    super.on(CONNECTION_METHOD_NAMES.LIVE_BETS_RESULT, handler);
  }

  sendHold(data) {
    super.invoke(CONNECTION_METHOD_NAMES.HOLD, data).then(() => {});
  }

  doCashOut(data) {
    super.invoke(CONNECTION_METHOD_NAMES.CASH_OUT, data).then(() => {});
  }

  handleHoldResult(handler) {
    super.on(CONNECTION_METHOD_NAMES.HOLD_RESULT, handler);
  }

  handleCashOutResult(handler) {
    super.on(CONNECTION_METHOD_NAMES.CASH_OUT_RESULT, handler);
  }

  doDemoBet(amount) {
    super.invoke(CONNECTION_METHOD_NAMES.DO_DEMO_BET, amount);
  }

  doDemoHold(data) {
    super.invoke(CONNECTION_METHOD_NAMES.DEMO_HOLD, data).then(() => {});
  }

  doDemoCashout() {
    super.invoke(CONNECTION_METHOD_NAMES.DEMO_CASH_OUT);
  }

  onDemoBetResult(handler) {
    super.on(CONNECTION_METHOD_NAMES.DEMO_BET_RESULT, handler);
  }

  onDemoHoldResult(handler) {
    super.on(CONNECTION_METHOD_NAMES.DEMO_HOLD_RESULT, handler);
  }

  onDemoCashoutResult(handler) {
    super.on(CONNECTION_METHOD_NAMES.DEMO_CASH_OUT_RESULT, handler);
  }

  doGamble(data) {
    super.invoke(CONNECTION_METHOD_NAMES.GET_GAMBLE_CARDS, data).then(() => {});
  }

  doDemoGamble(data) {
    super.invoke(CONNECTION_METHOD_NAMES.GET_DEMO_GAMBLE_CARDS, data).then(() => {});
  }

  getGambleResult(data) {
    super.invoke(CONNECTION_METHOD_NAMES.GAMBLE, data).then(() => {});
  }

  getDemoGambleResult(data) {
    super.invoke(CONNECTION_METHOD_NAMES.DEMO_GAMBLE, data).then(() => {});
  }

  onGambleCardsResult(handler) {
    super.on(CONNECTION_METHOD_NAMES.GAMBLE_CARDS_RESULT, handler);
  }

  onDemoGambleCardsResult(handler) {
    super.on(CONNECTION_METHOD_NAMES.DEMO_GAMBLE_CARDS_RESULT, handler);
  }

  onGambleResult(handler) {
    super.on(CONNECTION_METHOD_NAMES.GAMBLE_RESULT, handler);
  }

  onDemoGambleResult(handler) {
    super.on(CONNECTION_METHOD_NAMES.DEMO_GAMBLE_RESULT, handler);
  }

  executeGameConnections() {
    super.onBalanceUpdate();
    super.onBetsClosed(gameService.handleBetsClosed.bind(gameService));
    super.onRoundFinished(gameService.handleRoundFin.bind(gameService));
    super.onReconnected(gameService.handleReconnected.bind(gameService));
    super.onDoBetResult(gameService.handleDoBetResult.bind(gameService));
    super.onRoundStarted(gameService.handleRoundStart.bind(gameService));
    super.onResultReport(gameService.handleResultReport.bind(gameService));
    super.onDoBetResultValidation(gameService.handleDoBetResultValidation.bind(gameService));
  }

  executeTurboGameConnections() {
    this.onDemoBetResult(turboGameService.handleDoDemoBetResult.bind(turboGameService));
    this.onDemoHoldResult(turboGameService.handleDemoHoldResult.bind(turboGameService));
    this.onDemoCashoutResult(turboGameService.handleDemoCashoutResult.bind(turboGameService));
    this.onLiveBetsResult(turboGameService.handleLiveBetsResult.bind(turboGameService));
    this.handleHoldResult(turboGameService.handleHoldResult.bind(turboGameService));
    this.handleCashOutResult(turboGameService.handleCashOutResult.bind(turboGameService));
    this.onGambleCardsResult(turboGameService.handleGambleCardsResult.bind(turboGameService));
    this.onDemoGambleCardsResult(
      turboGameService.handleDemoGambleCardsResult.bind(turboGameService)
    );
    this.onGambleResult(turboGameService.handleGambleResult.bind(turboGameService));
    this.onDemoGambleResult(turboGameService.handleDemoGambleResult.bind(turboGameService));
    super.onDoBetResult(turboGameService.handleDoBetResult.bind(turboGameService));
    super.onDoBetResultValidation(
      turboGameService.handleDoBetResultValidation.bind(turboGameService)
    );
  }

  executeGlobalConnections() {
    super.onGameOpen();
    super.onMaintenanceNotify();
    super.onFreeBetEnded();
    super.onFreeBetAwarded();
    super.onFreeBetCountUpdate();
    super.onGetBalanceResult();
    super.onClose(gameService.handleConnectionError.bind(gameService));
    super.onReconnected(gameService.handleReconnected.bind(gameService));
    super.onReconnecting(gameService.handleReconnecting.bind(gameService));
  }
}

const { socketUrl } = environmentBuilder(gameName);
const { [DefaultQueryParams.GameId]: gameId } = parseQuery(window.location.search);
const isTurbo = +gameId === TURBO_GAME_ID;

const url = (path) => ({ url: `${socketUrl}${path}` });

export const gameLiveConnection = new GameConnection(
  url(isTurbo ? socketTurboPath : socketGamePath)
);
